import React, { useState, useEffect } from 'react';
import { useSignal } from "@preact/signals-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import { FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaTimes, FaSearch } from "react-icons/fa";
import PageForm from "../../templates/PageForm";
import CurrencyInput from "../../molecules/CurrencyInput";
import DayPicker from "../../molecules/DayPicker";
import SelectTextField from "../../molecules/SelectTextField";
import TextEdit from "../../molecules/TextEdit";
import FileUpload from "../../molecules/FileUpload";
import BadgeRegistrationStatus from "../../atoms/BadgeRegistrationStatus";
import ReceivablesDownloader from "./ReceivablesDownloader";
import DuplicateModalView from "./DuplicateModalView";

const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL;
const API_URL = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_URL;
const API_KEY = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_KEY;
const API_KEY_PERSON = process.env.REACT_APP_API_KEY;
const PERSON_SERVICE_URL = process.env.REACT_APP_PERSON_API_URL;

type DuplicateItem = {
    document_id: string;
    document_title: string;
    cfop: string;
    issue_date: string;
    xml_size_bytes: number;
    assignor_name: string;
    assignor_cnpj: string;
    debtor_cpf_cnpj: string;
    debtor_name: string;
    debtor_email: string;
    address_street: string;
    address_number: string;
    address_complement?: string;
    address_district: string;
    address_ibge_code: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
    address_country: string;
    address_phone: string;
    receivable_number: string;
    receivable_due_date: string;
    receivable_amount: number;
    receivable_v_pag: string;
    receivable_nat_op: string;
    receivable_ind_pag: string;
    disbursement_value: number;
    fee_name: string;
    fee_value: number;
    guarantee_percent: number;
    main_risk: string;
    external_link: string;
};

type Disbursement = {
    amount: number;
    date: string;
};

type Form = {
    total_amount: number;
    expiration_date: string;
    emission_date: string;
    created_at: string;
    guarantee_percentage: number;
    operation_number: string;
    status?: string;
    sequencial_number: number;
    external_link: string;
    type: string;
    assignor: string;
    recipient: string;
    originator: string;
    rebate_amount: number;
    assignment_type_name: string;
    with_recourse: boolean;
    duplicates: {
        quantity: number;
        total: number;
        items: DuplicateItem[];
    };
    disbursement: {
        disbursements: Disbursement[];
        totalAmount: number;
    };
};

const ReceivablesAssignmentPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.credit_operation_id || params.id;
    const isLoadingForm = useSignal(false);
    const [searchResults, setSearchResults] = useState<Array<{ value: string, label: string, personData?: any }>>([]);
    const [searchDestinatario, setSearchDestinatario] = useState('');
    const [selectedDestinatario, setSelectedDestinatario] = useState('');
    const [selectedCedente, setSelectedCedente] = useState('');
    const [assignorData, setAssignorData] = useState<any>(null);
    const [recipientData, setRecipientData] = useState<any>(null);
    const [selectedPersonData, setSelectedPersonData] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalSearchValue, setModalSearchValue] = useState('');
    const [modalSearchResults, setModalSearchResults] = useState<Array<{ value: string, label: string, personData?: any }>>([]);
    const newDisbursement = useSignal<{ amount: number; date: string | null }>({ amount: 0, date: new Date().toLocaleDateString('pt-BR') });
    const contractFiles = useSignal<File[]>([]);
    const artifactFiles = useSignal<File[]>([]);
    const [existingContractFile, setExistingContractFile] = useState<string>('');
    const [existingArtifactFiles, setExistingArtifactFiles] = useState<string[]>([]);
    const [removedContractFile, setRemovedContractFile] = useState<boolean>(false);
    const [removedArtifactFiles, setRemovedArtifactFiles] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [selectedDuplicate, setSelectedDuplicate] = useState<DuplicateItem | null>(null);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);


    const form = useSignal<Form>({
        total_amount: 0,
        expiration_date: '',
        emission_date: '',
        created_at: '',
        guarantee_percentage: 0,
        operation_number: "",
        sequencial_number: 0,
        external_link: "",
        type: "",
        assignor: "",
        recipient: "",
        originator: "",
        rebate_amount: 0,
        assignment_type_name: "",
        with_recourse: false,
        duplicates: {
            quantity: 1,
            total: 0,
            items: [{
                document_id: "",
                document_title: "",
                cfop: "",
                issue_date: "",
                xml_size_bytes: 0,
                assignor_name: "",
                assignor_cnpj: "",
                debtor_cpf_cnpj: "",
                debtor_name: "",
                debtor_email: "",
                address_street: "",
                address_number: "",
                address_district: "",
                address_ibge_code: "",
                address_city: "",
                address_state: "",
                address_zip_code: "",
                address_country: "",
                address_phone: "",
                receivable_number: "",
                receivable_due_date: "",
                receivable_amount: 0,
                receivable_v_pag: "",
                receivable_nat_op: "",
                receivable_ind_pag: "",
                disbursement_value: 0,
                fee_name: "",
                fee_value: 0,
                guarantee_percent: 0,
                main_risk: "",
                external_link: ""
            }]
        },
        disbursement: {
            disbursements: [],
            totalAmount: 0
        }
    });

    const handleChange = (field: string, value: any) => {
        form.value = { ...form.value, [field]: value };
    };

    const handleDuplicateChange = (index: number, field: string, value: any) => {
        const items = [...form.value.duplicates.items];
        items[index] = { ...items[index], [field]: value };
        const total = items.reduce((sum, item) => sum + item.receivable_amount, 0);
        form.value = {
            ...form.value,
            duplicates: { ...form.value.duplicates, items, total }
        };
    };

    const updateDuplicatesWithFormData = (items: DuplicateItem[]) => {
        return items.map(item => ({
            ...item,
            main_risk: "",
            external_link: form.value.external_link || ""
        }));
    };

    const handleJsonDataLoaded = (data: any) => {
        if (data.duplicates?.items) {
            const updatedItems = updateDuplicatesWithFormData(data.duplicates.items);
            form.value = {
                ...form.value,
                total_amount: data.total_amount || form.value.total_amount,
                operation_number: data.operation_code || form.value.operation_number,
                duplicates: {
                    quantity: data.duplicates.quantity || data.duplicates.items.length,
                    total: data.duplicates.total || 0,
                    items: updatedItems
                }
            };
        }
    };



    const addDisbursement = () => {
        if (newDisbursement.value.amount <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }
        if (!newDisbursement.value.date) {
            toast.error("Informe a data do desembolso");
            return;
        }

        const currentDisbursements = [...form.value.disbursement.disbursements];
        const newItem = { amount: newDisbursement.value.amount, date: newDisbursement.value.date };
        currentDisbursements.push(newItem);
        const totalAmount = currentDisbursements.reduce((sum, d) => sum + d.amount, 0);

        form.value = {
            ...form.value,
            disbursement: {
                disbursements: currentDisbursements,
                totalAmount
            }
        };
        newDisbursement.value = { amount: 0, date: new Date().toLocaleDateString('pt-BR') };
    };

    const handleSubmit = async () => {
        if (isFormDisabled) {
            toast.error('Não é possível salvar. Status não permite alterações.');
            return;
        }

        if (!selectedDestinatario) {
            toast.error('Beneficiário é obrigatório');
            return;
        }

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        // Validar tamanho dos arquivos (máximo 6MB)
        const maxSize = 6 * 1024 * 1024; // 6MB em bytes
        let totalSize = 0;

        contractFiles.value.forEach(file => {
            totalSize += file.size;
        });

        artifactFiles.value.forEach(file => {
            totalSize += file.size;
        });

        if (totalSize > maxSize) {
            toast.error(`O tamanho total dos arquivos (${(totalSize / 1024 / 1024).toFixed(2)}MB) excede o limite de 6MB`);
            return;
        }

        isLoadingForm.value = true;
        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const payload = {
                type: form.value.type || "",
                assignor: selectedCedente || "00000000-0000-0000-0000-000000000000", // UUID do cedente
                recipient: recipientData.id || "00000000-0000-0000-0000-000000000000", // UUID do destinatário
                credit_operation_id: credit_operation_id,
                operation_number: form.value.operation_number || "",
                operation_type: "RECEIVABLES_ASSIGNMENT",
                operation_schema_id: 2,
                approved_on: null,
                approved_value: null,
                current_balance: null,
                debt_balance_id: null,
                assignment_type_name: form.value.assignment_type_name || "",
                with_recourse: form.value.with_recourse,
                due_date: form.value.expiration_date || null,
                participants: [
                    {
                        id: assignorData?.id || null,
                        name: assignorData?.name || form.value.assignor,
                        document: assignorData?.document || "",
                        is_active: true,
                        person_id: selectedCedente || "00000000-0000-0000-0000-000000000000",
                        is_primary: true,
                        effective_to: null,
                        effective_from: assignorData?.effective_from || new Date().toISOString(),
                        guarantee_value: null,
                        participant_type_id: 3,
                        participation_order: 1,
                        guarantee_percentage: form.value.guarantee_percentage,
                        participant_type_name: "ASSIGNOR",
                        receives_disbursement: false
                    },
                    {
                        id: recipientData?.id || null,
                        name: recipientData?.name || form.value.recipient,
                        document: recipientData?.document || "",
                        is_active: recipientData?.is_active || true,
                        person_id: selectedDestinatario || "00000000-0000-0000-0000-000000000000",
                        is_primary: recipientData?.is_primary || false,
                        effective_to: recipientData?.effective_to || null,
                        effective_from: recipientData?.effective_from || new Date().toISOString(),
                        guarantee_value: null,
                        participant_type_id: 5,
                        participation_order: 1,
                        guarantee_percentage: null,
                        participant_type_name: "RECIPIENT",
                        receives_disbursement: false
                    }
                ],
                operation_dictionary: {
                    duplicates: {
                        items: form.value.duplicates.items.map(item => ({
                            document_id: item.document_id || "",
                            document_title: item.document_title || "",
                            cfop: item.cfop || "",
                            issue_date: item.issue_date || "",
                            xml_size_bytes: item.xml_size_bytes || 0,
                            assignor_name: item.assignor_name || "",
                            assignor_cnpj: item.assignor_cnpj || "",
                            debtor_cpf_cnpj: item.debtor_cpf_cnpj || "",
                            debtor_name: item.debtor_name || "",
                            debtor_email: item.debtor_email || "",
                            address_street: item.address_street || "",
                            address_number: item.address_number || "",
                            address_complement: item.address_complement || "",
                            address_district: item.address_district || "",
                            address_ibge_code: item.address_ibge_code || "",
                            address_city: item.address_city || "",
                            address_state: item.address_state || "",
                            address_zip_code: item.address_zip_code || "",
                            address_country: item.address_country || "",
                            address_phone: item.address_phone || "",
                            receivable_number: item.receivable_number || "",
                            receivable_due_date: item.receivable_due_date || "",
                            receivable_amount: item.receivable_amount || 0,
                            receivable_v_pag: item.receivable_v_pag || "",
                            receivable_nat_op: item.receivable_nat_op || "",
                            receivable_ind_pag: item.receivable_ind_pag || "",
                            disbursement_value: item.disbursement_value || 0,
                            fee_name: item.fee_name || "",
                            fee_value: item.fee_value || 0,
                            guarantee_percent: item.guarantee_percent || 0,
                            main_risk: item.main_risk || "",
                            external_link: item.external_link || ""
                        })),
                        total: form.value.duplicates.total || 0,
                        quantity: form.value.duplicates.quantity || 1
                    },
                    disbursement: {
                        totalAmount: form.value.disbursement.totalAmount || null,
                        disbursements: form.value.disbursement.disbursements.map(d => ({
                            date: d.date || null,
                            amount: d.amount || null
                        }))
                    },
                    total_amount: form.value.total_amount || null,
                    emission_date: form.value.emission_date || null,
                    external_link: form.value.external_link || "",
                    rebate_amount: form.value.rebate_amount || null,
                    expiration_date: form.value.expiration_date || null,
                    operation_number: form.value.operation_number || "",
                    originator: form.value.originator || "",
                    guarantee_percentage: form.value.guarantee_percentage || null,
                    files: {
                        // Usar novo arquivo ou manter existente se não foi removido
                        contract_file: contractFiles.value.length > 0
                            ? `${form.value.sequencial_number}${contractFiles.value[0].name.substring(contractFiles.value[0].name.lastIndexOf('.'))}`
                            : (removedContractFile ? '' : existingContractFile),
                        // Manter artefatos existentes não removidos e adicionar novos
                        artefacts: [
                            ...existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)),
                            ...artifactFiles.value.map(file => file.name)
                        ]
                    },
                }
            };

            console.log('Submitting payload:', payload);

            let requestBody;
            let headers: Record<string, string> = {
                'Authorization': `Bearer ${token}`,
                'x-api-key': API_KEY || ''
            };

            if (contractFiles.value.length > 0 || artifactFiles.value.length > 0) {
                const formData = new FormData();
                if (contractFiles.value.length > 0) {
                    formData.append('contract_file', contractFiles.value[0]);
                }
                artifactFiles.value.forEach((file, index) => {
                    formData.append('artifacts', file);
                });
                formData.append('data', JSON.stringify(payload));
                requestBody = formData;
            } else {
                headers['Content-Type'] = 'application/json';
                requestBody = JSON.stringify(payload);
            }

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/update`, {
                method: 'POST',
                headers,
                body: requestBody
            });

            if (response.ok) {
                toast.success("Cessão de recebíveis atualizada com sucesso!");
                navigate(-1);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar cessão');
            }
        } catch (error) {
            console.error('Erro ao salvar:', error);
            toast.error("Erro ao conectar com o servidor");
        } finally {
            isLoadingForm.value = false;
        }
    };

    const handlePersonSearch = async (searchValue: string) => {
        setSearchDestinatario(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const response = await fetch(`${BACKOFFICE_URL}/services/person/ALL/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY || ''
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const personItems = data.map((item: any) => {
                        const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name}`,
                            personData: item
                        };
                    });
                    //setSearchResults(personItems);
                    form.value = { ...form.value, recipient: personItems[0]?.personData.name };
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setSearchResults([]);
        }
    };

    const handleSelectParticipant = (selected: any) => {
        setSelectedDestinatario(selected.value);
        const [, name] = selected.label.split(' - ');
        handleChange("recipient", name || '');

        if (selected.personData) {
            const cnpjDoc = selected.personData.documents?.find((doc: any) => doc.type === 'CNPJ');
            const cpfDoc = selected.personData.documents?.find((doc: any) => doc.type === 'CPF');
            const document = cnpjDoc?.number || cpfDoc?.number || '';
            setSearchDestinatario(document);

            setRecipientData({
                ...recipientData,
                id: recipientData != null && selected.personData.id !== recipientData.id ? null : recipientData?.id,
                name: selected.personData.name,
                document: document,
                person_id: selected.personData.uuid,
                is_active: true,
                is_primary: false,
                effective_from: new Date().toISOString()
            });
            console.log('Selected recipient data set to:', recipientData);
        }
        setSearchResults([]);
    };

    const handleModalSearch = async (searchValue: string) => {
        setModalSearchValue(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const response = await fetch(`${PERSON_SERVICE_URL}/person/RECIPIENT/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY_PERSON || '',
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const personItems = data.results.map((item: any) => {
                        const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name}`,
                            personData: item
                        };
                    });
                    setModalSearchResults(personItems);
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setModalSearchResults([]);
        }
    };

    const handleModalSelectParticipant = (selected: any) => {
        handleSelectParticipant(selected);
        setIsModalOpen(false);
        setModalSearchValue('');
        setModalSearchResults([]);
    };

    const fetchContractData = async () => {
        console.log('fetchContractData called with:', { credit_operation_id, API_URL: !!API_URL, API_KEY: !!API_KEY });
        if (!credit_operation_id || !API_URL || !API_KEY) {
            console.log('fetchContractData early return due to missing params');
            return;
        }

        try {
            isLoadingForm.value = true;
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${API_URL}/contracts/receivables_assignment/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Contract data fetched:', data);
                const schema = data.operation_dictionary || {};
                const assignorParticipant = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                const recipientData = data.participants?.find((p: any) => p.participant_type_name === 'RECIPIENT');
                form.value = {
                    ...form.value,
                    operation_number: data.operation_number,
                    status: data.status || '',
                    sequencial_number: data.sequencial_number || 0,
                    assignor: assignorParticipant?.name || '',
                    originator: schema.originator || '',
                    with_recourse: data.with_recourse || false,
                    assignment_type_name: data.assignment_type || '',
                    created_at: data.created_at ? new Date(data.created_at).toLocaleDateString('pt-BR') : new Date().toLocaleDateString('pt-BR'),
                    expiration_date: schema.expiration_date || '',
                    emission_date: schema.emission_date || '',
                    total_amount: schema.total_amount || data.nominal_value || 0,
                    external_link: schema.external_link || '',
                    rebate_amount: schema.rebate_amount || 0,
                    guarantee_percentage: schema.guarantee_percentage || assignorParticipant?.guarantee_percentage || 0,
                    duplicates: {
                        quantity: schema.duplicates?.quantity || 1,
                        total: schema.duplicates?.total || 0,
                        items: schema.duplicates?.items || [{
                            document_id: "",
                            document_title: "",
                            cfop: "",
                            issue_date: "",
                            xml_size_bytes: 0,
                            assignor_name: "",
                            assignor_cnpj: "",
                            debtor_cpf_cnpj: "",
                            debtor_name: "",
                            debtor_email: "",
                            address_street: "",
                            address_number: "",
                            address_district: "",
                            address_ibge_code: "",
                            address_city: "",
                            address_state: "",
                            address_zip_code: "",
                            address_country: "",
                            address_phone: "",
                            receivable_number: "",
                            receivable_due_date: "",
                            receivable_amount: 0,
                            receivable_v_pag: "",
                            receivable_nat_op: "",
                            receivable_ind_pag: "",
                            disbursement_value: 0,
                            fee_name: "",
                            fee_value: 0,
                            guarantee_percent: 0,
                            main_risk: "",
                            external_link: ""
                        }]
                    },
                    disbursement: {
                        disbursements: schema.disbursement?.disbursements || [],
                        totalAmount: schema.disbursement?.totalAmount || 0
                    }
                };

                // Armazenar arquivos existentes do operation_dictionary
                if (schema.files) {
                    setExistingContractFile(schema.files.contract_file || '');
                    setExistingArtifactFiles(schema.files.artefacts || []);
                }

                // Armazenar dados dos participantes separadamente
                if (assignorParticipant) {
                    setAssignorData(assignorParticipant);
                    setSelectedCedente(assignorParticipant.person_id);
                }

                if (recipientData) {
                    setRecipientData(recipientData);
                    setSelectedDestinatario(recipientData.person_id);
                    setSearchDestinatario(recipientData.document);
                    form.value = {
                        ...form.value,
                        recipient: recipientData.name || ''
                    };
                }
            } else {
                toast.error('Erro ao carregar dados do contrato');
            }
        } catch (error) {
            console.error('Erro ao buscar contrato:', error);
            toast.error('Erro ao conectar com o servidor');
        } finally {
            isLoadingForm.value = false;
        }
    };

    useEffect(() => {
        console.log('useEffect triggered');
        console.log('All params:', params);
        console.log('credit_operation_id:', credit_operation_id);
        if (credit_operation_id) {
            fetchContractData();
        }
    }, [credit_operation_id]);

    const handleCancel = () => {
        navigate(-1);
    };

    const isFormDisabled = ['Suspenso', 'Cancelado', 'Finalizado com sucesso', 'Expirado'].includes(form.value.status || '');

    return (
        <PageForm
            pageTitle="Cessão de Recebíveis"
            apiVersion=""
            isLoading={isLoadingForm.value}
            breadcrumb={[
                { label: "Operações", url: "/operations" },
                { label: "Cessão de Recebíveis", url: "#" }
            ]}
            controlSlot={
                <div className="pt-4 flex justify-end">
                    <button
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleCancel}
                        disabled={isLoadingForm.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSubmit}
                        disabled={isLoadingForm.value || isFormDisabled}
                    >
                        Salvar
                    </button>
                </div>
            }
        >
            <ReceivablesDownloader
                sequencialNumber={form.value.sequencial_number}
                duplicatesCount={form.value.duplicates.items.filter(item => item.document_id || item.receivable_amount > 0).length}
                onDataLoaded={handleJsonDataLoaded}
            />
            <div className="container mx-auto px-2 py-4">
                <h2 className="text-xl font-semibold mt-4">Informações Gerais</h2>
                <div className="mb-4 mt-2">

                    <BadgeRegistrationStatus
                        statusId={form.value.status === 'APPROVED' ? 4 : form.value.status === 'REJECTED' ? 5 : 1}
                        status={form.value.status || ''}
                    />
                </div>
                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-2">
                        <TextEdit
                            label="Número do Contrato"
                            id="sequencial_number"
                            value={form.value.sequencial_number.toString()}
                            disabled={true}
                        />
                    </div>
                    <div className="col-span-2">
                        <TextEdit
                            label="Código da Cessão"
                            value={form.value.operation_number}
                            onChange={(e) => handleChange("operation_number", e.target.value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <TextEdit
                            label="Card JIRA"
                            value={form.value.external_link}
                            onChange={(e) => handleChange("external_link", e.target.value)}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mt-6">
                    <div className="col-span-3">
                        <TextEdit
                            label="CPF/CNPJ Originador"
                            value={form.value.originator}
                            onChange={(e) => handleChange("originator", e.target.value)}
                            placeholder=""
                        />
                    </div>
                    <div className="col-span-2 flex items-center pt-6">
                        <input
                            type="checkbox"
                            id="with_recourse"
                            checked={form.value.with_recourse}
                            onChange={(e) => handleChange("with_recourse", e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label htmlFor="with_recourse" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                            Com Regresso
                        </label>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mt-6">
                    <div className="col-span-5">
                        <TextEdit
                            label="Cedente"
                            value={form.value.assignor}
                            onChange={(e) => handleChange("assignor", e.target.value)}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mt-6">
                    <div className="col-span-2">
                        <TextEdit
                            label="Buscar Beneficiário"
                            value={searchDestinatario}
                            onChange={(e) => handlePersonSearch(e.target.value)}
                            placeholder=""
                        />
                    </div>
                    <div className="col-span-6 flex items-end gap-2">
                        <div className="flex-1">
                            <TextEdit
                                label="Beneficiário"
                                value={form.value.recipient}
                                disabled={true}
                            />
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 mb-4"
                            title="Buscar beneficiário"
                        >
                            <FaSearch />
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mt-6">
                    <div className="col-span-2">
                        <TextEdit
                            label="Data de Criação"
                            value={form.value.created_at}
                            disabled={true}
                        />
                    </div>
                    <div className="col-span-2">
                        <DayPicker
                            label="Data de Emissão"
                            value={form.value.emission_date}
                            onChange={(value) => handleChange("emission_date", value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            label="% aval da operação"
                            prefix="%"
                            fractionDigits={2}
                            value={form.value.guarantee_percentage}
                            onChange={(value) => handleChange("guarantee_percentage", value)}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4">Desembolsos</h2>

                <div className="grid grid-cols-12 gap-4 mt-4 p-4 border rounded bg-gray-50">
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor do Desembolso"
                            prefix="R$"
                            fractionDigits={2}
                            value={newDisbursement.value.amount}
                            onChange={(value) => {
                                newDisbursement.value = { ...newDisbursement.value, amount: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <DayPicker
                            label="Data do Desembolso"
                            value={newDisbursement.value.date}
                            onChange={(value) => {
                                newDisbursement.value = { ...newDisbursement.value, date: value };
                            }}
                        />
                    </div>
                    <div className="col-span-2 flex items-end">
                        <button
                            onClick={addDisbursement}
                            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mb-4"
                        >
                            Incluir
                        </button>
                    </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Valor</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {form.value.disbursement.disbursements.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                                        Nenhum desembolso adicionado
                                    </td>
                                </tr>
                            ) : (
                                form.value.disbursement.disbursements.map((disbursement, index) => (
                                    <tr key={`${index}-${disbursement.amount}-${disbursement.date}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {disbursement.amount != null && disbursement.amount >= 0
                                                ? `R$ ${disbursement.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                : <span className="text-gray-400">--</span>
                                            }
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            {disbursement.date}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    const disbursements = form.value.disbursement.disbursements.filter((_, i) => i !== index);
                                                    const totalAmount = disbursements.reduce((sum, d) => sum + d.amount, 0);
                                                    form.value = {
                                                        ...form.value,
                                                        disbursement: { disbursements, totalAmount }
                                                    };
                                                }}
                                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                            >
                                                Remover
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Total dos Desembolsos"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.disbursement.totalAmount}
                            disabled={true}
                        />
                    </div>
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor do Abatimento"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.rebate_amount}
                            onChange={(value) => handleChange("rebate_amount", value)}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4">Vencimento</h2>
                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-2">
                        <CurrencyInput
                            label="Valor Total"
                            prefix="R$"
                            fractionDigits={2}
                            value={form.value.total_amount}
                            onChange={(value) => handleChange("total_amount", value)}
                        />
                    </div>
                    <div className="col-span-2">
                        <DayPicker
                            label="Data de Expiração"
                            value={form.value.expiration_date}
                            onChange={(value) => handleChange("expiration_date", value)}
                        />
                    </div>
                </div>

                <h2 className="text-xl font-semibold mt-4">Contrato</h2>
                <div className="mt-4">
                    <FileUpload
                        id="contract-upload"
                        name="contract"
                        label="Upload do Contrato"
                        accept=".pdf,.doc,.docx"
                        errorMessage=""
                        onUploadFile={(file) => contractFiles.value = [file]}
                        onRemoveFile={() => contractFiles.value = []}
                    />
                    {existingContractFile && !removedContractFile && (
                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded flex justify-between items-center">
                            <p className="text-sm text-blue-700">
                                <strong>Arquivo atual:</strong> {existingContractFile}
                            </p>
                            <button
                                onClick={() => setRemovedContractFile(true)}
                                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                            >
                                <FaTimes className="inline mr-1" /> Remover
                            </button>
                        </div>
                    )}
                    {removedContractFile && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded flex justify-between items-center">
                            <p className="text-sm text-red-700">
                                <strong>Arquivo removido:</strong> {existingContractFile}
                            </p>
                            <button
                                onClick={() => setRemovedContractFile(false)}
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                            >
                                Restaurar
                            </button>
                        </div>
                    )}
                </div>

                <h2 className="text-xl font-semibold mt-4">Artefatos</h2>
                {removedArtifactFiles.length > 0 && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                        <p className="text-sm font-medium text-red-700 mb-2">Arquivos removidos:</p>
                        <ul className="text-sm text-red-600 space-y-1">
                            {removedArtifactFiles.map((fileName, index) => (
                                <li key={index} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                                        {fileName}
                                    </div>
                                    <button
                                        onClick={() => setRemovedArtifactFiles(removedArtifactFiles.filter(f => f !== fileName))}
                                        className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                    >
                                        Restaurar
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-4">
                    <div className="flex items-center justify-center w-full">
                        <label htmlFor="artifacts-upload"
                            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500">
                                    <span className="font-semibold">Clique aqui para subir arquivos</span> ou arraste e solte aqui.
                                </p>
                            </div>
                            <input
                                id="artifacts-upload"
                                type="file"
                                className="hidden"
                                multiple
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    artifactFiles.value = [...artifactFiles.value, ...files];
                                }}
                            />
                        </label>
                    </div>
                </div>
                {existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)).length > 0 && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm font-medium text-green-700 mb-2">Arquivos atuais:</p>
                        <ul className="text-sm text-green-600 space-y-1">
                            {existingArtifactFiles.filter(file => !removedArtifactFiles.includes(file)).map((fileName, index) => (
                                <li key={index} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                        {fileName}
                                    </div>
                                    <button
                                        onClick={() => setRemovedArtifactFiles([...removedArtifactFiles, fileName])}
                                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                                    >
                                        <FaTimes className="inline" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {artifactFiles.value.length > 0 && (
                    <div className="mt-4 p-4 border rounded bg-gray-50">
                        <h3 className="text-sm font-medium mb-3">Arquivos Selecionados ({artifactFiles.value.length})</h3>
                        <div className="flex flex-wrap gap-4">
                            {artifactFiles.value.map((file, index) => {
                                const getFileIcon = (fileName: string) => {
                                    const ext = fileName.split('.').pop()?.toLowerCase();
                                    switch (ext) {
                                        case 'pdf': return <FaFilePdf className="text-red-500 text-2xl" />;
                                        case 'doc':
                                        case 'docx': return <FaFileWord className="text-blue-500 text-2xl" />;
                                        case 'xls':
                                        case 'xlsx': return <FaFileExcel className="text-green-500 text-2xl" />;
                                        case 'jpg':
                                        case 'jpeg':
                                        case 'png':
                                        case 'gif': return <FaFileImage className="text-purple-500 text-2xl" />;
                                        default: return <FaFile className="text-gray-500 text-2xl" />;
                                    }
                                };

                                return (
                                    <div key={index} className="relative flex flex-col items-center p-3 bg-white border rounded shadow-sm hover:shadow-md transition-shadow">
                                        <button
                                            onClick={() => artifactFiles.value = artifactFiles.value.filter((_, i) => i !== index)}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                                        >
                                            <FaTimes />
                                        </button>
                                        {getFileIcon(file.name)}
                                        <span className="text-xs mt-2 text-center max-w-20 truncate" title={file.name}>
                                            {file.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <h2 className="text-xl font-semibold mt-4">Duplicatas</h2>

                <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">#</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Sacado</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nome Cedente</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nº Título</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Valor Desembolso</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Emissão</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Data Vencimento</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Parcela</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">UF</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Cidade</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {form.value.duplicates.items
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((item, displayIndex) => {
                                    const actualIndex = (currentPage - 1) * itemsPerPage + displayIndex;
                                    return (
                                        <tr
                                            key={actualIndex}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => {
                                                setSelectedDuplicate(item);
                                                setIsDuplicateModalOpen(true);
                                            }}
                                        >
                                            <td className="px-2 py-2 whitespace-nowrap text-sm font-medium text-gray-600">{actualIndex + 1}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.debtor_name}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.assignor_name}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.document_title}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_amount ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.receivable_amount) : 'R$ 0,00'}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.issue_date ? new Date(item.issue_date).toLocaleDateString('pt-BR') : ''}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_due_date ? new Date(item.receivable_due_date).toLocaleDateString('pt-BR') : ''}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.receivable_number}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_state}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-sm">{item.address_city}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                        <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                            <tr>
                                <td colSpan={9} className="px-2 py-3 text-sm font-semibold text-gray-700">
                                    Total de registros: {form.value.duplicates.items.length}
                                </td>
                                <td className="px-2 py-3 text-sm font-semibold text-gray-700 text-right">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                                        form.value.duplicates.items.reduce((sum, item) => sum + (item.receivable_amount || 0), 0)
                                    )}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Paginação */}
                {form.value.duplicates.items.length > itemsPerPage && (
                    <div className="mt-4 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                            Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, form.value.duplicates.items.length)} de {form.value.duplicates.items.length} duplicatas
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Anterior
                            </button>
                            <span className="px-3 py-1 text-sm">
                                Página {currentPage} de {Math.ceil(form.value.duplicates.items.length / itemsPerPage)}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(form.value.duplicates.items.length / itemsPerPage)))}
                                disabled={currentPage === Math.ceil(form.value.duplicates.items.length / itemsPerPage)}
                                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}

            </div>

            {/* Modal de busca de destinatário */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-3/4 max-w-4xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Buscar Beneficiário</h3>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setModalSearchValue('');
                                    setModalSearchResults([]);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="mb-4">
                            <TextEdit
                                label="CPF/CNPJ ou Nome"
                                value={modalSearchValue}
                                onChange={(e) => handleModalSearch(e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full bg-white border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-auto">Documento</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-full">Nome</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b h-12 w-auto">Ação</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {Array.from({ length: 6 }, (_, index) => {
                                        const result = modalSearchResults[index];
                                        return (
                                            <tr key={index} className="hover:bg-gray-50 h-12">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-auto">
                                                    {result ? result.label.split(' - ')[0] : ''}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-full truncate">
                                                    {result ? result.label.split(' - ')[1] : ''}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm w-auto">
                                                    {result && (
                                                        <button
                                                            onClick={() => handleModalSelectParticipant(result)}
                                                            className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                                                        >
                                                            Selecionar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de visualização de duplicata */}
            <DuplicateModalView
                isOpen={isDuplicateModalOpen}
                onClose={() => {
                    setIsDuplicateModalOpen(false);
                    setSelectedDuplicate(null);
                }}
                duplicate={selectedDuplicate}
            />

        </PageForm>
    );
};

export default ReceivablesAssignmentPage;